import { Token, TokenType } from './tokens'

/**
 * Tokenizes the Minidown source.
 *
 * @param source
 */
export function tokenize(source: string): Token[] {
  const result: Token[] = []
  // Position in the stream
  let pos = 0
  // The end of the stream
  const eos = source.length
  // Whether we are iterating on the last character.
  let isLastCh = false
  // Buffer for text tokens.
  let textBuf = ''

  while (pos < eos) {
    isLastCh = pos + 1 === eos
    const ch = source.charCodeAt(pos)
    switch (ch) {
      case CharacterCodes.Asterisk:
      case CharacterCodes.Underscore: {
        // If the next character is the same as this one, it's a strong marker.
        // If it's the last character in the stream, then it's an emphasis marker.
        const nextCh = isLastCh ? -1 : source.charCodeAt(pos + 1)
        const isStrong = nextCh === ch
        const value = isStrong
          ? String.fromCharCode(ch).repeat(2)
          : String.fromCharCode(ch)
        pos += value.length

        // If there is any buffered text, flush it as a text token.
        if (textBuf) {
          result.push({
            type: TokenType.Text,
            value: textBuf,
          })
          // Reset the text buffer.
          textBuf = ''
        }

        result.push({
          value,
          type: isStrong ? TokenType.StrongMarker : TokenType.EmphasisMarker,
        })

        break
      }
      default:
        // Anything else is text, so buffer it until we reach another token type,
        // or until we reach the end.
        textBuf += String.fromCharCode(ch)
        pos++
    }
  }

  // Check for any unflushed text.
  if (textBuf) {
    result.push({
      type: TokenType.Text,
      value: textBuf,
    })
  }

  return cleanup(result)
}

/**
 * Whether 2 tokens are equal.
 *
 * @param left
 * @param right
 */
export function tokenEquals(left: Token, right: Token) {
  return left.type === right.type && left.value === right.value
}

/**
 * Cleans up the token stream by doing some additional passes over it.
 * Converts unterminated markers to text and combines tokens.
 *
 * This is the worst part of parsing Markdown, because it doesn't just error on
 * malformed input, we actually have to convert it to text.
 *
 * @param tokens
 */
function cleanup(tokens: Token[]): Token[] {
  const result: Token[] = []
  let pos = 0
  const eos = tokens.length

  // Tracks whether we are inside a specific type of marker (**, *, __, _)
  const markerTrack = new Map<string, boolean>()

  while (pos < eos) {
    const startToken = tokens[pos++]
    if (startToken.type !== TokenType.Text) {
      // Whether we were tracking this type of marker.
      const wasTracking = markerTrack.get(startToken.value)
      markerTrack.set(startToken.value, !wasTracking)

      // Get the index of the corresponding end token.
      const endTokenIndex = findIndexWithOffset(tokens, pos, (t) =>
        tokenEquals(startToken, t),
      )

      // Get the index of ANY tracked end token.
      const nextEndTokenIndex = findIndexWithOffset(
        tokens,
        pos,
        (t) => !!markerTrack.get(t.value),
      )

      // Convert to text if...
      if (
        // ... the end index of this token is later than the next end token of ANY marker,
        // then this one is unterminated.
        (nextEndTokenIndex !== -1 && endTokenIndex > nextEndTokenIndex) ||
        // Or, if this is the start of a pair, but no end token was found.
        (!wasTracking && endTokenIndex === -1)
      ) {
        result.push({
          type: TokenType.Text,
          value: startToken.value,
        })
        markerTrack.set(startToken.value, false)
        continue
      }
    }
    result.push(startToken)
  }

  return consolidateConsecutiveTextTokens(result)
}

/**
 * Combines consecutive text tokens.
 *
 * @param result
 */
function consolidateConsecutiveTextTokens(result: Token[]): Token[] {
  const consolidated: Token[] = []
  for (let i = 0; i < result.length; i++) {
    const next = result[i]
    if (i > 0 && next.type === TokenType.Text) {
      const prev = consolidated[consolidated.length - 1]
      if (prev.type === TokenType.Text) {
        prev.value += next.value
        continue
      }
    }

    consolidated.push(next)
  }
  return consolidated
}

/**
 * Like find, but with an offset.
 *
 * @param source
 * @param offsetInclusive
 * @param predicate
 */
function findIndexWithOffset(
  source: Token[],
  offsetInclusive: number,
  predicate: (token: Token) => boolean,
) {
  for (let i = offsetInclusive; i < source.length; i++) {
    const token = source[i]
    if (predicate(token)) {
      return i
    }
  }
  return -1
}

/**
 * Special character codes.
 */
export const enum CharacterCodes {
  Asterisk = 42,
  Underscore = 95,
}
